import { Injectable } from '@angular/core';


import { TokenType } from '@shared/models/configs/social-auth.configs';
import {
  ExternalSignupRequest,
  RegisterRequest,
  FBProfile,
  GoogleProfResponse,
  GoogleAuthenticationResponse,
  LocalSignupFormModel,
  ExternalSignUpIncompleteFormRequest
} from '@shared/models/register-models';

import { NgRedux } from '@angular-redux/store';
import { IAppState, AuthResponse, Actions, User } from '@core/redux/stores';

import { ProxyService } from '@core/services/proxy.service';
import { AlertService } from '@core/services/alert.service';
import { ModalService } from '@core/services/modal.service';
import { AuthTrackingService } from '@core/services/authTracking.service';
import { ConfirmModalService } from '@core/services/confirm-modal.service';

import { ApiRoutes } from '@shared/models/configs/api.endpoints';

import { FacebookService, InitParams, LoginOptions, LoginResponse } from 'ngx-facebook';
import { environment } from '@environments/environment';

import { BehaviorSubject, Subscription } from 'rxjs';
import * as Store from '@core/redux/stores';
import { LoggingService } from '@core/services/logging.service';
import { RequestUser } from '@features/authenticate/models';
import { MFATokenPayload } from '@shared/cms/models/mfa-token-payload';
import { ErrorCodesService } from './error-codes.service';
import { ModalTrackingGtmDto, GtmExternal } from '@shared/models/configs/gtm.external';
import { GtmService } from './gtm.service';
import { MFAErrorcodeEnum } from './mfa-errorcode-enum';
import { Guid } from 'ez-guid';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { first } from 'rxjs/operators';
import { Constants } from '@core/constants/core.constants';
import { UtilitiesService } from './utilities.service';
import { BrazeService } from './braze.service';
import { CiamFlagsService } from './ciam-flags.service';

// Used for google+ social sign up. This gets us access to the googleapi object defined when the javascript loads
declare var gapi: any;
declare const AppleID: any;
declare let google: any;

interface CheckAcctResp {
  exists: boolean;
  emailVerified: boolean;
}

/** Result of {@link AuthenticationService.handleAppleSignInRedirect} for CIAM V6 routing. */
export type AppleRedirectResult =
  | { status: 'success' }
  | { status: 'error' }
  | { status: 'terms_pending' }
  | { status: 'incomplete' }
  | {
      status: 'social_challenge';
      maskedEmail: string;
      pendingSocialChallengeToken: string;
      facebookBindingToken?: string;
    };

@Injectable()
export class AuthenticationService {

  private alertService: AlertService;

  public extRequest: ExternalSignupRequest;
  private intRequest: RegisterRequest;
  private social: boolean;
  public doesAccountPreexist: boolean;
  private hasError: boolean;

  private errorMessage: string;
  private errorHeader: string;

  private registrationReady: boolean;
  private registerSignIn: boolean;

  /** Pending Auth0 passwordless signup (after OTP verify, before T&amp;C). */
  private auth0OtpSignupProfile: {
    pendingSignupToken: string;
    firstName: string;
    lastName: string;
    phone: string;
    zipCode: string;
    emailOptIn: boolean;
  } | null = null;

  /** Post-order guest signup: order context carried through CIAM Sign In/Sign Up until auth completes. */
  private guestOrderCtx: {
    orderId: string;
    guestUserGuid: string;
    orderIdentifiers: { orderId: string; userIdOrGuid: string }[];
  } | null = null;

  private readonly GUEST_ORDER_CTX_KEY = 'wb_guest_order_ctx';
  private readonly GUEST_ORDER_CTX_TTL_MS = 30 * 60 * 1000;

  setGuestOrderContext(orderId: string, guestUserGuid: string): void {
    const ctx = {
      orderId,
      guestUserGuid,
      orderIdentifiers: [{ orderId, userIdOrGuid: guestUserGuid }],
      setAt: Date.now(),
    };
    this.guestOrderCtx = ctx;
    try { sessionStorage.setItem(this.GUEST_ORDER_CTX_KEY, JSON.stringify(ctx)); } catch { /* quota / private browsing */ }
  }

  clearGuestOrderContext(): void {
    this.guestOrderCtx = null;
    try { sessionStorage.removeItem(this.GUEST_ORDER_CTX_KEY); } catch { /* ignore */ }
  }

  /**
   * One-time restore of guest order context from sessionStorage after an OAuth redirect (e.g. Apple).
   * Removes the storage entry immediately after reading so it cannot be reused for a different signup.
   * Expired entries (> 30 min) are discarded and cleared.
   */
  private tryRestoreGuestOrderContext(): void {
    if (this.guestOrderCtx) return;
    try {
      const raw = sessionStorage.getItem(this.GUEST_ORDER_CTX_KEY);
      if (!raw) return;
      sessionStorage.removeItem(this.GUEST_ORDER_CTX_KEY);
      const stored = JSON.parse(raw);
      if (!stored?.orderId || !stored?.guestUserGuid) return;
      if (stored.setAt && Date.now() - stored.setAt > this.GUEST_ORDER_CTX_TTL_MS) return;
      this.guestOrderCtx = {
        orderId: stored.orderId,
        guestUserGuid: stored.guestUserGuid,
        orderIdentifiers: stored.orderIdentifiers ?? [{ orderId: stored.orderId, userIdOrGuid: stored.guestUserGuid }],
      };
    } catch { /* parse error — discard */ }
  }

  getGuestOrderContext(): {
    orderId: string;
    guestUserGuid: string;
    orderIdentifiers: { orderId: string; userIdOrGuid: string }[];
  } | null {
    return this.guestOrderCtx;
  }

  getGuestOrderPayload(): {
    orderId: number;
    guestUserGuid: string;
    orderIdentifiers: { orderId: number; userIdOrGuid: string }[];
  } | null {
    if (!this.guestOrderCtx) return null;
    return {
      orderId: Number(this.guestOrderCtx.orderId),
      guestUserGuid: this.guestOrderCtx.guestUserGuid,
      orderIdentifiers: this.guestOrderCtx.orderIdentifiers.map(oi => ({
        orderId: Number(oi.orderId),
        userIdOrGuid: oi.userIdOrGuid,
      })),
    };
  }

  appendGuestOrderToPayload<T extends Record<string, unknown>>(payload: T): T {
    const ctx = this.getGuestOrderPayload();
    return ctx ? { ...payload, ...ctx } : payload;
  }

  private resendSub: Subscription;

  public facebookProfile: FBProfile;
  private googleProfile: any;
  /** One Tap / Sign in with Google: single initialize per page; used for Google ID token (JWT) on Auth0 CTE. */
  private googleAccountsIdInitialized = false;
  guestSignUpSub=new BehaviorSubject(false);
  checkifAccountSub=new BehaviorSubject(false);

  private generalFBErrMsg = 'Hmm, something\'s not working with Facebook, right now. Want to give it another try?';
  private generalGGErrMsg = 'Hmm, something\'s not working with Google, right now. Want to give it another try?';
  private unableToVerify = 'Hmm, unable to verify if account already exists. Want to give it another try?';
  private acctInUseMsg = 'Uh-oh, that email is already being used for another account. ' +
    'Please make sure you entered it correctly and try again or sign into your existing account.';

  private fbloaded: boolean;
  private isEmailVerificationOn: boolean;
  public mfaToken: MFATokenPayload;
  public resolvedUser: User;
  tokenClient: any;
  constructor(
    private proxy: ProxyService,
    private ngRedux: NgRedux<IAppState>,
    private fbook: FacebookService,
    private modalService: ModalService,
    private authTrackingService: AuthTrackingService,
    private confirmModalService: ConfirmModalService,
    private logger: LoggingService,
    private gtmService: GtmService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private brazeService: BrazeService,
    private ciamFlagsService: CiamFlagsService) {
    this.isEmailVerificationOn = false;
    this.social = false;
    this.doesAccountPreexist = false;
    this.hasError = false;
    this.registrationReady = false;
    this.errorMessage = null;
    this.errorHeader = null;
    this.registerSignIn = false;
    setTimeout(() => {
      this.checkExternalApisLoaded();
    }, 1);
    this.resolvedUser = null;
    this.sendGTMUserDatalayerPush();
  }

  private sendGTMUserDatalayerPush() {
    this.authTrackingService.auth.subscribe(
      auth => {
        if (auth?.tokenInfo?.accessToken) {
          this.authTrackingService.user.pipe(first()).subscribe(user => {
            this.sendGTMSignInEvent(user);
          });
        }
      }
    );
  }

  public sendGTMSignInEvent(user: User, excludeNameAndMethod: boolean = true) {
    if (user && user.id) {
      const event: any = {
        user_id: user.id,
        // method: user.mainAccountType == 'Local' ? 'Email' : user.mainAccountType,
        loyalty_userID: true,
        is_known_user: true
      };
      if (!excludeNameAndMethod) {
        event.event = 'sign_in';
        event.method = user.mainAccountType == 'Local' ? 'Email' : user.mainAccountType;

      }
      this.gtmService.sendGenericEvent(event);
    }
  }

  checkExternalApisLoaded() {
    this.checkIfFacebookLoaded();
  }

  checkIfFacebookLoaded() {
    const initParams: InitParams = {
      appId: environment.FB_KEY,
      version: environment.FB_API_VERSION,
      status: true
    };

    this.fbloaded = window['FB'];

    // call the facebook service to initialize witht he parameters from above
    if (this.fbloaded) {
      this.fbook.init(initParams);
    }
  }

  getErrorHeader() {
    return this.errorHeader;
  }

  getErrorMessage() {
    return this.errorMessage;
  }

  private showModal(name: string, autoHide: boolean = true) {
    this.modalService.setModalSelector(name);
    this.modalService.showModal(autoHide, 1000);
  }

  setAlertService(service: AlertService) {
    this.alertService = service;
  }

  isSocial() {
    return this.social;
  }

  getExternalRequest() {
    return this.extRequest;
  }

  getInteralRequest() {
    return this.intRequest;
  }

  isReady() {
    return this.registrationReady;
  }

  setRegisterSignIn(state) {
    this.registerSignIn = state;
  }

  getRegisterSignIn() {
    return this.registerSignIn;
  }

  setExternalRequest(req: ExternalSignupRequest) {
    this.extRequest = req;
    this.social = true;
    this.registrationReady = true;
  }

  setExternalMissingFields(req: ExternalSignUpIncompleteFormRequest) {
    this.logger.log('Filling missing signin fields: ', req);
    const extParams = <Array<any>>this.extRequest.externalParams;
    if (req.firstName) {
      extParams.find(a => a.name === 'firstName').value = req.firstName;
    }
    if (req.lastName) {
      extParams.find(a => a.name === 'lastName').value = req.lastName;
    }
    if (req.email) {
      extParams.find(a => a.name === 'email').value = req.email;
    }
    this.extRequest.externalParams = extParams;
    this.social = true;
    this.registrationReady = true;
  }

  async setInternalRequest(local: LocalSignupFormModel, showTerms: boolean = false) {
    this.errorMessage = null;

    this.intRequest = new RegisterRequest(
      local.firstname,
      local.lastname,
      local.email,
      local.zipcode,
      local.passwords.password,
      local.addToEmail,
      local.phonenumber,
      local.passwords.confPw,
    );
     this.social = false;
    await this.checkIfAccountExists(this.intRequest.email, 'Local');

    if (this.hasError) {
      this.errorMessage = this.unableToVerify;
      this.alertService.error(this.errorMessage);
    } else if (this.doesAccountPreexist) {
      this.errorMessage = this.acctInUseMsg;
      this.alertService.error(this.errorMessage);
    } else {
      if (showTerms) {
        this.showModal('app-term-n-condition');
      }
      this.registrationReady = true;
    }
  }

  async setGuestSignup(local: LocalSignupFormModel, showTerms: boolean = false) {
    this.errorMessage = null;

    this.intRequest = new RegisterRequest(
      local.firstname,
      local.lastname,
      local.email,
      local.zipcode,
      local.passwords.password,
      local.addToEmail,
      local.phonenumber,
      local.passwords.confPw,
    );
    if (local.orderId) {
      this.intRequest.orderId = local.orderId;
    }

    if (local.guestUserGuid) {
      this.intRequest.guestUserGuid = local.guestUserGuid;
    }
    if(local.orderId && local.guestUserGuid)
    {
      this.intRequest.orderIdentifiers=[
        {
          orderId: local.orderId,
          userIdOrGuid: local.guestUserGuid,
        },
      ]
    }
    await this.checkIfAccountExists(local.email, 'Local');

    if (this.hasError) {
      this.guestSignUpSub.next(false);
      this.errorMessage = this.unableToVerify;
      this.alertService.error(this.errorMessage);
    } else if (this.doesAccountPreexist) {
      this.errorMessage = this.acctInUseMsg;
      this.alertService.error(this.errorMessage);
      this.guestSignUpSub.next(false);
      this.checkifAccountSub.next(true);
    } else {
      this.registrationReady = true;
      this.completeRegistration(true).then(succ=>
        {
         this.guestSignUpSub.next(true);
        },(err)=>
        {
          this.guestSignUpSub.next(false);
        });
    }
  }

  setExternalFB(profile: FBProfile, token: string) {
    this.facebookProfile = profile;

    const request = new ExternalSignupRequest();

    const externalParams = [];
    externalParams.push(
      {
        'name': 'deviceId',
        'value': 'iPhone'
      },
      {
        'name': 'firstName',
        'value': profile.first_name
      },
      {
        'name': 'lastName',
        'value': profile.last_name
      },
      {
        'name': 'email',
        'value': profile.email
      },
      {
        'name': 'zipcode',
        'value': undefined
      }
    );

    request.accessTokenType = TokenType.FACEBOOK;
    request.accessToken = token;
    request.externalParams = externalParams;

    this.setExternalRequest(request);
  }

  setExternalGP(resp: any, accessToken: string, googleIdToken?: string | null) {

    let bday = null;
    if (resp?.result?.birthdays[0]?.date?.year) {
      let month = resp.result.birthdays[0].date.month.toString();
      if (month.length === 1) {
        month = '0' + month;
      }

      let day = resp.result.birthdays[0].date.day.toString();
      if (day.length === 1) {
        day = '0' + day;
      }
      bday = [
        resp.result.birthdays[0].date.year,
        month,
        day
      ].join('-');
    } else if (resp?.result?.birthdays[1]?.date?.year) {
      let month = resp.result.birthdays[1].date.month.toString();
      if (month.length === 1) {
        month = '0' + month;
      }
      let day = resp.result.birthdays[1].date.day.toString();
      if (day.length === 1) {
        day = '0' + day;
      }
      bday = [
        resp.result.birthdays[1].date.year,
        month,
        day
      ].join('-');
    } else {
      bday = undefined;
    }
    let firstName = null;
    let lastName = null;
    let emailAddress = null;
    firstName = resp?.result?.names[0]?.givenName ?? resp?.result?.names[1]?.givenName;
    lastName = resp?.result?.names[0]?.familyName ?? resp?.result?.names[1]?.familyName;
    emailAddress = resp?.result?.emailAddresses[0]?.value ?? resp?.result?.names[1]?.value;
    const request = new ExternalSignupRequest();

    const externalParams = [];
    externalParams.push(
      {
        'name': 'deviceId',
        'value': 'iPhone'
      },
      {
        'name': 'firstName',
        'value': firstName
      },
      {
        'name': 'lastName',
        'value': lastName
      },
      {
        'name': 'dateOfBirth',
        'value': bday
      },
      {
        'name': 'email',
        'value': emailAddress
      },
      {
        'name': 'zipcode',
        'value': undefined
      }
    );

    request.accessTokenType = TokenType.GOOGLE;
    request.accessToken = accessToken;
    if (googleIdToken) {
      request.idToken = googleIdToken;
    }
    request.externalParams = externalParams;
    this.setExternalRequest(request);
  }

  setExternalAP(accessToken: string, user: User, appleIdToken?: string | null) {
    const request = new ExternalSignupRequest();

    const externalParams = [];
    externalParams.push(
      {
        'name': 'firstName',
        'value': user.firstName
      },
      {
        'name': 'lastName',
        'value': user.lastName
      },
      {
        'name': 'email',
        'value': user.email
      },
      {
        'name': 'zipcode',
        'value': user.zipCode
      }
    );
    request.externalParams = externalParams;
    request.accessTokenType = TokenType.APPLE;
    request.accessToken = accessToken;
    if (appleIdToken) {
      request.idToken = appleIdToken;
    }
    this.setExternalRequest(request);
  }

  getFacebookProfile() {
    return this.facebookProfile;
  }

  setFacebookEmail(email: string) {
    this.facebookProfile.email = email;
    this.setExternalFB(this.facebookProfile, this.extRequest.accessToken);
  }

  private facebookLogin() {

    if (!this.fbloaded) {
      this.checkIfFacebookLoaded();
      if (!this.fbloaded) {
        console.log('Error loading facebook SDK');
        return;
      }
    }
    const options: LoginOptions = {
      scope: 'public_profile,email',
      return_scopes: true,
      enable_profile_selector: true,
      auth_type: 'reauthenticate'
    };
    this.alertService.clear();

    return this.fbook.login(options).then(
      (response) => {
        return <LoginResponse>response;
      },
      (error: any) => {
        if (error) {
          if (error.message) {
            this.errorMessage = error.message;
          } else if (error.error && error.error.message) {
            this.errorMessage = error.error.message;
          } else {
            this.errorMessage = this.generalFBErrMsg;
          }
        } else {
          this.errorMessage = this.generalFBErrMsg;
        }
        this.alertService.error(this.errorMessage);
      }
    );
  }

  private getFacebookDetails() {
    return this.fbook.api('/me', 'get', { fields: 'email,first_name,last_name,picture' }).then(
      (profileResponse) => {
        return <FBProfile>profileResponse;
      },
      (error: any) => {
        if (error) {
          this.errorMessage = error.error.message;
        } else {
          this.errorMessage = this.generalFBErrMsg;
        }
        this.alertService.error(this.errorMessage);
        return undefined;
      }
    );
  }

  async facebookRegister(showTerms: boolean = false, signIn: boolean = false) {
    this.errorMessage = null;
    const authResp = await this.facebookLogin() as LoginResponse;

    if (authResp === undefined) {
      return;
    }

    this.facebookProfile = await this.getFacebookDetails();

    if (this.facebookProfile === undefined) {
      return;
    }

    if (this.isCiamV6Com()) {
      this.setExternalFB(this.facebookProfile, authResp.authResponse.accessToken);
      return;
    }

    await this.checkIfAccountExists(authResp.authResponse.accessToken, 'Facebook');

    if (this.hasError) {
      this.errorMessage = this.unableToVerify;
      this.alertService.error(this.errorMessage);
    } else if (this.doesAccountPreexist) {
      if (signIn === false) {
        this.errorMessage = this.acctInUseMsg;
        this.alertService.error(this.errorMessage);
      } else {
        this.setExternalFB(this.facebookProfile, authResp.authResponse.accessToken);
      }
    } else {
      if (signIn === false) {
        if (this.facebookProfile.email !== undefined && this.facebookProfile.email !== null) {
          await this.checkIfAccountExists(this.facebookProfile.email, 'Local');
        }

        if (this.hasError) {
          this.errorMessage = this.unableToVerify;
          this.alertService.error(this.errorMessage);
        } else if (this.doesAccountPreexist) {
          this.errorMessage = this.acctInUseMsg;
          this.alertService.error(this.errorMessage);
        } else {
          this.setExternalFB(this.facebookProfile, authResp.authResponse.accessToken);
          if (showTerms) {
            if (this.facebookProfile.email === undefined) {
              this.showModal('app-signin-incomplete', false);
            } else {
              this.showModal('app-term-n-condition', false);
            }
          }
        }
      } else {
        this.setExternalFB(this.facebookProfile, authResp.authResponse.accessToken);
        if (this.facebookProfile.email === undefined) {
          this.showModal('app-signin-incomplete', false);
        } else {
          this.showModal('app-term-n-condition', false);
        }
      }
    }
  }
  private getTokenFromClient(tokenClient): Promise<any> {
    return new Promise((resolve, reject) => {
      try {
        tokenClient.requestAccessToken();
        tokenClient.callback = (resp) => {
          if (resp.error !== undefined) {
            reject(resp);
          }          
          resolve(resp);
        };
        tokenClient.error_callback = (err) => {          
          if (err?.error !== undefined) {
            reject(err);
          }          
          resolve(err);
        };
        
      } catch (err) {
        console.log(err);
        resolve(err);
      }
    });   
  }
  private getGoogleTokenClient() {
    return new Promise<void>((resolve, reject) => {
      try {
        const baseScopes = (environment.GP_SCOPES || '').trim();
        const tokens = baseScopes.split(/\s+/).filter(Boolean);
        const rest = tokens.filter((t) => t.toLowerCase() !== 'openid');
        // OIDC id_token is only returned when openid is granted; put it first (GIS + mixed Google API scopes).
        const scope = ['openid', ...rest].join(' ').trim();
        const resp = google.accounts.oauth2.initTokenClient({
            client_id: environment.GP_CLIENT_ID,
            scope,
            callback: () => {},
            error_callback: this.logger?.log
        });
        resolve(resp);
      } catch (err) {
        reject(err);
      }
    });
  }

  /**
   * Auth0 CTE expects a Google ID token (JWT). GIS token client usually returns only an access token.
   * If the token response includes id_token (some configs), use it; otherwise try One Tap (same client_id).
   */
  private async resolveGoogleJwtForAuth0Cte(tokenResponse: any): Promise<string | null> {
    const fromToken = tokenResponse?.id_token;
    if (typeof fromToken === 'string' && fromToken.startsWith('eyJ')) {
      return fromToken;
    }
    return this.tryObtainGoogleJwtViaOneTap();
  }

  private tryObtainGoogleJwtViaOneTap(): Promise<string | null> {
    return new Promise((resolve) => {
      if (typeof google === 'undefined' || !google.accounts?.id) {
        resolve(null);
        return;
      }
      let settled = false;
      const finish = (jwt: string | null) => {
        if (settled) {
          return;
        }
        settled = true;
        resolve(jwt && typeof jwt === 'string' ? jwt : null);
      };
      const timer = window.setTimeout(() => finish(null), 5000);
      if (!this.googleAccountsIdInitialized) {
        google.accounts.id.initialize({
          client_id: environment.GP_CLIENT_ID,
          auto_select: true,
          cancel_on_tap_outside: true,
          callback: (resp: { credential?: string }) => {
            window.clearTimeout(timer);
            finish(resp?.credential ?? null);
          },
        });
        this.googleAccountsIdInitialized = true;
      }
      google.accounts.id.prompt();
    });
  }
  private async googleLogin(showTerms: boolean, signIn: boolean): Promise<any> {
    try {
      const tokenClient = await this.getGoogleTokenClient();
      const response = await this.getTokenFromClient(tokenClient);      
      if(!response.access_token) {
        this.setErrorAndAlert(this.generalGGErrMsg);
        return;
      }
      let googleJwtCache: string | null | undefined = undefined;
      const ensureGoogleJwtForCte = async (): Promise<string | null> => {
        if (googleJwtCache === undefined) {
          googleJwtCache = await this.resolveGoogleJwtForAuth0Cte(response).catch(() => null);
        }
        return googleJwtCache;
      };
      if (this.isCiamV6Com()) {
        //pass ID token if available (some GIS configs return it); backend falls back to access-token CTE when absent.
        const googleIdToken = await this.resolveGoogleJwtForAuth0Cte(response).catch(() => null);
        this.googleProfile = await this.getGoogleProfile(response.access_token);
        if (!this.googleProfile) {
          return;
        }
        this.setExternalGP(this.googleProfile, response.access_token, googleIdToken);
        return;
      }

      try {
        const doesAccountExist = await this.proxy.httpPost(ApiRoutes.ACC_EXISTS, {
          accessTokenOrEmail: response.access_token,
          loginType: 'Google'
        }).toPromise();
        this.doesAccountPreexist = false;
        const resp = <CheckAcctResp>doesAccountExist.body.valueOf();
        if (resp.exists) {
          this.doesAccountPreexist = resp.exists;
        }
      } catch (error) {
        this.doesAccountPreexist = false;
        this.hasError = true;
      }
      if (this.hasError) {
        this.setErrorAndAlert(this.unableToVerify);
      } else if (this.doesAccountPreexist) {
        if (signIn === false) {
          this.setErrorAndAlert(this.acctInUseMsg);
        } else {
          this.googleProfile = await this.getGoogleProfile(response.access_token);
          this.setExternalGP(this.googleProfile, response.access_token, await ensureGoogleJwtForCte());
          await this.completeRegistration();
        }
      } else {
        const resolve =         await this.proxy.httpPost(ApiRoutes.RESOLVE_EXTERNAL, {
          token: response.access_token,
          tokenType: 'GoogleAccessToken'
        }).toPromise();
        if (resolve.status === 200) {
          this.resolvedUser = <User>resolve.body.valueOf();
        }
        if (signIn === false) {
          if (this.resolvedUser.email) {
            await this.checkIfAccountExists(this.resolvedUser.email, 'Local');
          }
          if (this.hasError) {
            this.errorMessage = this.unableToVerify;
            this.alertService.error(this.errorMessage);
          } else if (this.doesAccountPreexist) {
            this.errorMessage = this.acctInUseMsg;
            this.alertService.error(this.errorMessage);
          } else {
            this.googleProfile = await this.getGoogleProfile(response.access_token);
            this.setExternalGP(this.googleProfile, response.access_token, await ensureGoogleJwtForCte());
            if (showTerms) {
              this.showModal('app-term-n-condition', false);
            }
            }
          } else {
          // added this line so first time user if try to signin with out sign up they will also get term and condition popup
          this.googleProfile = await this.getGoogleProfile(response.access_token);
          this.setExternalGP(this.googleProfile, response.access_token, await ensureGoogleJwtForCte());
          if (!this.doesAccountPreexist) {
            this.showModal('app-term-n-condition', true);
          } else {
              await this.completeRegistration();
          }
        }
      }
    } catch (error) {
      this.setErrorAndAlert(this.generalGGErrMsg);
      return;
    }
  }
  private setErrorAndAlert(errorMessage) {
    this.errorMessage = errorMessage;
    this.alertService.error(this.errorMessage);
  }

  private async getGoogleProfile(accessToken) {
    gapi.client.setToken({'access_token':accessToken});
    try {
      const response = await gapi.client.people.people.get({ resourceName: 'people/me', personFields: 'birthdays,names,emailAddresses' });
      return <GoogleProfResponse>response;
    } catch (error) {
      if (error) {
        this.errorMessage = error?.error?.message;
      } else {
        this.errorMessage = this.generalGGErrMsg;
      }
      this.alertService.error(this.errorMessage);
      return undefined;
    }    
  }

/** When true, CIAM V6: Apple register must not be blocked by Thinktecture-only `/accounts/exists`; JIT + Auth0 link run on `signup_external` (after T&C via {@link completeRegistration}). */
  isCiamV6Com(): boolean {
    return this.ciamFlagsService.isCiamV6Com();
  }

appleAuth(source: string) {
  const nonce = Guid.create().toString();
  const originQueryString = `${Constants.APPLE_SIGNIN_ORIGIN}=`;
  let state = decodeURIComponent(window.location.href);
  // Extract return Url from state and append all applesignin parameters ahead of those
  const urlParams = new URLSearchParams(window.location.href);
  let returnUrl;
  if(urlParams.has(Constants.RETURN_URL)) {
    returnUrl = urlParams.get(Constants.RETURN_URL);
    urlParams.delete(Constants.RETURN_URL);
    state = decodeURIComponent(urlParams.toString());
  } else if(this.activatedRoute?.snapshot?.queryParams[Constants.RETURN_URL]) {
    returnUrl = this.activatedRoute?.snapshot?.queryParams[Constants.RETURN_URL];        
    state = decodeURIComponent(`${location.protocol}//${location.host}${location.pathname}`);
  }
  if (state.indexOf('?') === -1) {
      state = `${state}?${originQueryString}${source}`;
  } else {
    state = `${state}&${originQueryString}${source}`;
  }
  if(returnUrl) {
    const rUrl = encodeURIComponent(`${Constants.RETURN_URL}=${returnUrl}`);
    state = `${state}&${rUrl}`;
  }
  localStorage.setItem('apple-signin-nonce', nonce);
  AppleID.auth.init({
    clientId: environment.APPLE_SIGNIN_SERVICE_ID,
    scope: environment.APPLE_SIGNIN_SCOPES ,
    redirectURI: environment.APPLE_SIGNIN_REDIRECTURI ,
    state: state,
    nonce: nonce
    });
  AppleID.auth.signIn();
}

public getAppleSignInQueryParams(): any {
  const params = {
    queryParams:
    UtilitiesService.generateQueryParams(
      [
        Constants.ACCESS_TOKEN,
        Constants.ID_TOKEN,
        Constants.SUCCESS,
        Constants.NONCE,
        Constants.CANCELLED,
        Constants.APPLE_SIGNIN_ORIGIN
      ],
      [null, null, null, null, null, null]
    ),
    queryParamsHandling: 'merge'
  };
  return params;
}

  async googleRegister(showTerms: boolean = false, signIn: boolean = false) {
    this.errorMessage = null;
    this.alertService.clear();
    await this.googleLogin(showTerms, signIn);
  }

  async appleSignInRegister(
    accessToken: string,
    showTerms: boolean = false,
    signIn: boolean = false,
    appleIdToken?: string | null
  ): Promise<AppleRedirectResult> {
    localStorage.removeItem('apple-signin-nonce');
    this.errorMessage = null;
    this.alertService.clear();
    this.hasError = false;
    this.doesAccountPreexist = false;

    if (!this.isCiamV6Com()) {
      await this.proxy.httpPost(ApiRoutes.ACC_EXISTS, {
        accessTokenOrEmail: accessToken,
        loginType: 'Apple'
      }).toPromise().then(
        (success) => {
          const resp = <CheckAcctResp>success.body.valueOf();
          if (resp.exists) {
            this.doesAccountPreexist = resp.exists;
          }
        },
        () => {
          this.hasError = true;
        }
      );
    }

    if (this.hasError) {
      this.errorMessage = this.unableToVerify;
      this.alertService.error(this.errorMessage);
      return { status: 'error' };
    }

    if (this.doesAccountPreexist && signIn) {
      await this.proxy.httpPost(ApiRoutes.RESOLVE_EXTERNAL, {
        token: accessToken,
        tokenType: 'AppleAccessToken'
      }).toPromise().then(
        (success) => {
          this.resolvedUser = <User>success.body.valueOf();
        },
        () => {
          this.hasError = true;
        }
      );
      if (!this.hasError) {
        this.setExternalAP(accessToken, this.resolvedUser, appleIdToken);
        await this.completeRegistration();
        this.modalService.hideModal();
      }
      return this.hasError ? { status: 'error' } : { status: 'success' };
    }

    if (this.doesAccountPreexist && !signIn && !this.isCiamV6Com()) {
      this.errorMessage = this.acctInUseMsg;
      this.alertService.error(this.errorMessage);
      return { status: 'error' };
    }

    return this.appleContinueAfterResolve(accessToken, showTerms, signIn, appleIdToken);
  }

  /**
   * CIAM V6 Apple: signup_external first, then T&amp;C + signup/complete for new users (same as Google CTE).
   */
  private async appleCiamV6AfterResolve(
    accessToken: string,
    appleIdToken: string | null | undefined,
    cleanupQueryParams: boolean
  ): Promise<AppleRedirectResult> {
    this.setExternalAP(accessToken, this.resolvedUser, appleIdToken);
    try {
      const result = await this.socialCteAsync();
      if (result.outcome === 'signed_in') {
        if (cleanupQueryParams) {
          this.cleanUpAppleSigninQueryParams();
        }
        this.modalService.hideModal();
        return { status: 'success' };
      }
      if (result.outcome === 'new_user') {
        this.prepareAuth0OtpSignupForTerms({
          pendingSignupToken: result.pendingSignupToken,
          firstName: (result.firstName ?? this.resolvedUser?.firstName ?? '').trim(),
          lastName: (result.lastName ?? this.resolvedUser?.lastName ?? '').trim(),
          phone: '',
          zipCode: (this.resolvedUser?.zipCode ?? '').trim(),
          emailOptIn: false,
        });
        if (cleanupQueryParams) {
          this.cleanUpAppleSigninQueryParams();
        }
        this.showModal('app-term-n-condition', false);
        return { status: 'terms_pending' };
      }
      if (result.outcome === 'challenge') {
        return {
          status: 'social_challenge',
          maskedEmail: result.maskedEmail,
          pendingSocialChallengeToken: result.pendingSocialChallengeToken,
          facebookBindingToken: result.facebookBindingToken,
        };
      }
    } catch (error) {
      this.processError(error);
      return { status: 'error' };
    }
    this.errorMessage = this.errorMessage ?? 'Unexpected response from Apple sign-in.';
    return { status: 'error' };
  }

  private isAppleProfileIncomplete(user: User | null | undefined): boolean {
    if (!user?.missingProperties?.length) {
      return false;
    }
    return user.missingProperties.includes('Email')
      || user.missingProperties.includes('FirstName')
      || user.missingProperties.includes('LastName');
  }

  /**
   * Resolve Apple profile, optional duplicate-local check (legacy only), then terms / incomplete / signup_external.
   * CIAM V6 uses {@link socialCteAsync} + signup/complete for deferred new-user signup.
   */
  private async appleContinueAfterResolve(
    accessToken: string,
    showTerms: boolean,
    signIn: boolean,
    appleIdToken?: string | null
  ): Promise<AppleRedirectResult> {
    const resolve = await this.proxy.httpPost(ApiRoutes.RESOLVE_EXTERNAL, {
      token: accessToken,
      tokenType: 'AppleAccessToken'
    }).toPromise();
    if (resolve.status === 200) {
      this.resolvedUser = <User>resolve.body.valueOf();
    }
    const cleanupQueryParams = this.router.url.toString().indexOf(Constants.RETURN_URL) === -1;
    if (signIn === false) {
      if (!this.isCiamV6Com() && this.resolvedUser.email) {
        await this.checkIfAccountExists(this.resolvedUser.email, 'Local');
      }
      if (this.hasError) {
        this.errorMessage = this.unableToVerify;
        this.alertService.error(this.errorMessage);
        return { status: 'error' };
      }
      if (!this.isCiamV6Com() && this.doesAccountPreexist) {
        this.errorMessage = this.acctInUseMsg;
        this.alertService.error(this.errorMessage);
        return { status: 'error' };
      }
      if (this.isCiamV6Com() && showTerms) {
        if (this.isAppleProfileIncomplete(this.resolvedUser)) {
          this.setExternalAP(accessToken, this.resolvedUser, appleIdToken);
          if (cleanupQueryParams) {
            this.cleanUpAppleSigninQueryParams();
          }
          this.showModal('app-signin-incomplete', false);
          return { status: 'incomplete' };
        }
        return this.appleCiamV6AfterResolve(accessToken, appleIdToken, cleanupQueryParams);
      }
      this.setExternalAP(accessToken, this.resolvedUser, appleIdToken);
      if (showTerms) {
        if (cleanupQueryParams) {
          this.cleanUpAppleSigninQueryParams();
        }
        if (this.isAppleProfileIncomplete(this.resolvedUser)) {
          this.showModal('app-signin-incomplete', false);
          return { status: 'incomplete' };
        }
        this.showModal('app-term-n-condition', false);
        return { status: 'terms_pending' };
      }
      return { status: 'success' };
    }

    // CIAM V6 sign-in: match Google CTE — signup_external resolves returning users even when
    // Apple tokens omit names (Apple only sends given_name/family_name on first authorization).
    if (this.isCiamV6Com()) {
      return this.appleCiamV6AfterResolve(accessToken, appleIdToken, cleanupQueryParams);
    }

    if (this.isAppleProfileIncomplete(this.resolvedUser)) {
      this.setExternalAP(accessToken, this.resolvedUser, appleIdToken);
      if (cleanupQueryParams) {
        this.cleanUpAppleSigninQueryParams();
      }
      this.showModal('app-signin-incomplete', false);
      return { status: 'incomplete' };
    }
    this.setExternalAP(accessToken, this.resolvedUser, appleIdToken);
    if (this.doesAccountPreexist) {
      if (cleanupQueryParams) {
        this.cleanUpAppleSigninQueryParams();
      }
      await this.completeRegistration();
      return this.errorMessage ? { status: 'error' } : { status: 'success' };
    }
    if (cleanupQueryParams) {
      this.cleanUpAppleSigninQueryParams();
    }
    this.showModal('app-term-n-condition', true);
    return { status: 'terms_pending' };
  }

  // this calls the web api with an email and token type of Facebook, Google, or Local
  // to see if an account already exists with that email and token type.
  private async checkIfAccountExists(email: string, loginType: string) {
    return await this.proxy.httpPost(ApiRoutes.ACC_EXISTS, {
      accessTokenOrEmail: email,
      loginType: loginType
    }).toPromise().then(
      (success) => {
        this.doesAccountPreexist = false;
        const resp = <CheckAcctResp>success.body.valueOf();
        if (resp.exists) {
          this.doesAccountPreexist = resp.exists;
        }
      },
      (error) => {
        this.doesAccountPreexist = false;
        this.hasError = true;
      }
    );
  }

  /**
   * Call after OTP signup profile step, before showing T&amp;C. On accept, {@link completeRegistration} posts to signup/complete.
   */
  prepareAuth0OtpSignupForTerms(profile: {
    pendingSignupToken: string;
    firstName: string;
    lastName: string;
    phone: string;
    zipCode: string;
    emailOptIn: boolean;
  }): void {
    this.social = false;
    this.auth0OtpSignupProfile = { ...profile };
    this.registrationReady = true;
  }

  /**
   * CIAM V6: posts to /accounts/signup_external using the pre-set extRequest (Google/Facebook/Apple).
   * Callers must first invoke googleRegister/facebookRegister to populate extRequest.
   * Returns a discriminated result so the component can drive the next step without knowing
   * about AuthResponse internals.
   */
  async socialCteAsync(): Promise<
    | { outcome: 'signed_in' }
    | { outcome: 'new_user'; pendingSignupToken: string; firstName?: string; lastName?: string; email?: string }
    | {
        outcome: 'challenge';
        maskedEmail: string;
        email?: string;
        pendingSocialChallengeToken: string;
        facebookBindingToken?: string;
      }
  > {
    this.tryRestoreGuestOrderContext();
    const request = this.getExternalRequest();
    const orderPayload = this.getGuestOrderPayload();
    const requestBody = orderPayload ? { ...request, ...orderPayload } : request;
    const response = await this.proxy.httpPost(ApiRoutes.AUTH_EXTERNAL, requestBody).toPromise();
    const status = (response as any)?.status ?? 200;
    const body = (response as any)?.body ?? response;

    if (status === 202 || body?.challengeRequired) {
      return {
        outcome: 'challenge',
        maskedEmail: body?.maskedEmail ?? '',
        email: (body?.email ?? '').toString().trim() || undefined,
        pendingSocialChallengeToken: body?.pendingSocialChallengeToken ?? '',
        facebookBindingToken: body?.facebookBindingToken ?? '',
      };
    }

    if (body?.pendingSignupToken) {
      const params: Array<{ name: string; value: any }> =
        Array.isArray(request?.externalParams) ? (request.externalParams as any[]) : [];
      const get = (n: string): string =>
        (params.find((p: any) => p.name === n)?.value as string | undefined) ?? '';
      return {
        outcome: 'new_user',
        pendingSignupToken: body.pendingSignupToken as string,
        firstName: get('firstName'),
        lastName: get('lastName'),
        email: get('email'),
      };
    }

    const signInResponse = body as AuthResponse;
    signInResponse.user.isEmailVerified = this.getEmailVerificationStatus(signInResponse.user.isEmailVerified);
    this.ngRedux.dispatch({ type: Actions.USERLOGGEDIN, data: signInResponse });
    this.updateBrazeUser(signInResponse.user);
    this.sendGTMSignInEvent(signInResponse.user, false);
    const guestUser = new Store.CustomerInfo();
    this.ngRedux.dispatch({ type: Actions.GUESTLOGIN, data: guestUser });
    this.clearGuestOrderContext();
    return { outcome: 'signed_in' };
  }

  /** Applies a successful signup_external / facebook-bind-email sign-in payload to Redux. */
  applySocialSignupSignInResponse(body: unknown): void {
    const signInResponse = body as AuthResponse;
    signInResponse.user.isEmailVerified = this.getEmailVerificationStatus(signInResponse.user.isEmailVerified);
    this.ngRedux.dispatch({ type: Actions.USERLOGGEDIN, data: signInResponse });
    this.updateBrazeUser(signInResponse.user);
    this.sendGTMSignInEvent(signInResponse.user, false);
    const guestUser = new Store.CustomerInfo();
    this.ngRedux.dispatch({ type: Actions.GUESTLOGIN, data: guestUser });
  }

  async completeRegistration(isFirstTimeSigningIn=false) {
    if (this.registrationReady) {
      if (this.auth0OtpSignupProfile) {
        this.tryRestoreGuestOrderContext();
        const p = this.auth0OtpSignupProfile;
        try {
          const response = await this.proxy.httpPost(ApiRoutes.AUTH0_SIGNUP_COMPLETE, {
            pendingSignupToken: p.pendingSignupToken,
            firstName: p.firstName,
            lastName: p.lastName,
            phone: p.phone || '',
            zipCode: p.zipCode,
            emailOptIn: p.emailOptIn,
            ...(this.getGuestOrderPayload() ?? {}),
          }).toPromise();
          const signInResponse = <AuthResponse>response.body.valueOf();
          signInResponse.user.isEmailVerified = this.getEmailVerificationStatus(signInResponse.user.isEmailVerified);
          this.ngRedux.dispatch({ type: Actions.USERLOGGEDIN, data: signInResponse });
          this.updateBrazeUser(signInResponse.user);
          if (isFirstTimeSigningIn) {
            const method = signInResponse.user.mainAccountType === 'Local' ? 'Email' : signInResponse.user.mainAccountType;
            this.gtmService.sendGenericEvent({
              event: 'sign_up',
              user_id: signInResponse.user.id,
              method,
              is_loyalty_member: true,
              is_known_user: true
            });
            if (p.emailOptIn) {
              this.gtmService.sendGenericEvent({ event: 'email_signup' });
            }
          } else if (signInResponse?.user?.id) {
            this.sendGTMSignInEvent(signInResponse.user, false);
          }
          const guestUser = new Store.CustomerInfo();
          this.ngRedux.dispatch({ type: Actions.GUESTLOGIN, data: guestUser });
          this.clearGuestOrderContext();
        } catch (error) {
          this.processError(error);
        } finally {
          this.auth0OtpSignupProfile = null;
          this.registrationReady = false;
          this.resolvedUser = null;
        }
        return;
      }

      let request = null;
      let apiUrl = null;
      if (this.social) {
        request = this.getExternalRequest();
        apiUrl = ApiRoutes.AUTH_EXTERNAL;
      } else {
        request = this.getInteralRequest();
        apiUrl = ApiRoutes.AUTH_INTERNAL;
      }

      try {
        const response = await this.proxy.httpPost(apiUrl, request).toPromise();
        const rawBody = (response as any)?.body;
        const body = rawBody?.valueOf ? rawBody.valueOf() : rawBody;

        //Legacy Apple register accepted T&C before signup_external; finish via signup/complete when deferred.
        if (this.social && body?.pendingSignupToken) {
          await this.completeSocialPendingSignupFromExternalResponse(body, isFirstTimeSigningIn);
          return;
        }

        const signInResponse = <AuthResponse>body;
        signInResponse.user.isEmailVerified = this.getEmailVerificationStatus(signInResponse.user.isEmailVerified);        
        this.ngRedux.dispatch({ type: Actions.USERLOGGEDIN, data: signInResponse });
        this.updateBrazeUser(signInResponse.user);
        if(isFirstTimeSigningIn) {
          this.gtmService.sendGenericEvent({
          event: "sign_up",
          user_id: signInResponse.user.id,
          method: signInResponse.user.mainAccountType == 'Local' ? 'Email' : signInResponse.user.mainAccountType,
          is_loyalty_member: true,
          is_known_user: true
          });
          if(this.getInteralRequest()?.hasSubcribedToEmail) {
            this.gtmService.sendGenericEvent({
              event: "email_signup",
              });
          }
        } else if (signInResponse?.user?.id) {
          this.sendGTMSignInEvent(signInResponse.user, false);
        }
        const guestUser = new Store.CustomerInfo();
        this.ngRedux.dispatch({ type: Actions.GUESTLOGIN, data:  guestUser});
      } catch (error) {
        this.processError(error);
      }
    } else {
      if (this.errorMessage === null) {
        this.errorMessage = 'Registration not ready due to request not being complete';
      }
    }

    this.registrationReady = false;
    this.resolvedUser = null;
  }

  /** Legacy social path: user already accepted T&C, then signup_external returned pendingSignupToken. */
  private async completeSocialPendingSignupFromExternalResponse(
    body: { pendingSignupToken: string; user?: User },
    isFirstTimeSigningIn: boolean
  ): Promise<void> {
    this.tryRestoreGuestOrderContext();
    const user = body.user ?? ({} as User);
    try {
      const response = await this.proxy.httpPost(ApiRoutes.AUTH0_SIGNUP_COMPLETE, {
        pendingSignupToken: body.pendingSignupToken,
        firstName: (user.firstName ?? '').trim(),
        lastName: (user.lastName ?? '').trim(),
        phone: '',
        zipCode: user.zipCode ?? '',
        emailOptIn: false,
        ...(this.getGuestOrderPayload() ?? {}),
      }).toPromise();
      const signInResponse = <AuthResponse>response.body.valueOf();
      signInResponse.user.isEmailVerified = this.getEmailVerificationStatus(signInResponse.user.isEmailVerified);
      this.ngRedux.dispatch({ type: Actions.USERLOGGEDIN, data: signInResponse });
      this.updateBrazeUser(signInResponse.user);
      if (isFirstTimeSigningIn) {
        const method = signInResponse.user.mainAccountType === 'Local' ? 'Email' : signInResponse.user.mainAccountType;
        this.gtmService.sendGenericEvent({
          event: 'sign_up',
          user_id: signInResponse.user.id,
          method,
          is_loyalty_member: true,
          is_known_user: true
        });
      } else if (signInResponse?.user?.id) {
        this.sendGTMSignInEvent(signInResponse.user, false);
      }
      const guestUser = new Store.CustomerInfo();
      this.ngRedux.dispatch({ type: Actions.GUESTLOGIN, data: guestUser });
      this.clearGuestOrderContext();
    } catch (error) {
      this.processError(error);
    } finally {
      this.registrationReady = false;
      this.resolvedUser = null;
    }
  }

  updateBrazeUser(user: User) {
    this.proxy.httpGet(ApiRoutes.BRAZE_USERS).subscribe(
      success => {
        var brazeUser = <Store.BrazeUser>success.body.valueOf();
        this.ngRedux.dispatch({type: Actions.BRAZEUSER, data: brazeUser});
        this.brazeService.brazeUser = brazeUser;
        this.brazeService.updateUser(user);
      }, error => {
        this.ngRedux.dispatch({type: Actions.CLEARBRAZEUSER});
      }
    );
  }

  processError(error: any) {
    this.logger.logError(error, 'error reg user ' + JSON.stringify(error));
    if (error.error.message) {
      this.errorMessage = error.error.message;
    } else if (error.error) {
      if (error.error.length && error.error.length > 0) {
        if (error.error[0].code) {
          if (error.error[0].code === 4102) {
            this.errorMessage = 'Uh-oh, the First name you entered doesn\'t meet our requirement. ' +
              'Please make sure you entered it correctly and try again';
            this.errorHeader = 'First name doesn\'t meet requirements';
          } else if (error.error[0].code === 4103) {
            this.errorMessage = 'Uh-oh, the Last name you entered doesn\'t meet our requirement. ' +
              'Please make sure you entered it correctly and try again';
            this.errorHeader = 'Last name doesn\'t meet requirements';
          } else if (error.error[0].code === 4105) {
            this.errorMessage = 'Uh-oh, the email you entered doesn\'t meet our requirement. ' +
              'Please make sure you entered it correctly and try again';
            this.errorHeader = 'Email doesn\'t meet requirements';
          } else {
            this.errorMessage = error.error[0].message;
          }
        } else {
          this.errorMessage = '';
          for (const msg of error.error) {
            this.errorMessage += msg.message;
          }
        }
      } else {
        this.errorMessage = error.error;
      }
    }
  }

  public resendConfirmation(email: string = null) {
    if (email === undefined || email === null || email.trim() === '') {
      email = this.authTrackingService.getUserEmail();
    }
    const payload = {
      email: email
    };

    this.resendSub = this.proxy.httpPost(ApiRoutes.RESENDCONFIRM, payload).subscribe(
      (success) => {
        this.confirmModalService.show(
          'Mail sent',
          '',
          'Email sent successfully.',
          'OK, Got It',
          '',
          '',
          true,
          'close'
        );
        this.resendSub.unsubscribe();
      },
      (err) => {
        if (err.status === 200) {
          this.confirmModalService.show(
            'Mail sent',
            '',
            'Email sent successfully.',
            'OK, Got It',
            '',
            '',
            true,
            'close'
          );
        } else {
          this.alertService.error(err.error.message);
        }
        this.resendSub.unsubscribe();
      }
    );
  }

  public getEmailVerificationStatus(emailVerified: boolean) {
    if (this.isEmailVerificationOn) {
      return emailVerified;
    }
    return true;
  }

  public showNewDevicePopup(email: string) {
    this.confirmModalService.show(
      'Using a new device?',
      '',
      email ? 'To protect your account, we\'ll send a temporary code to your email -<br>'
      + '<b>' + email + '</b>' +
      '<br>Enter it on the next screen to sign in with this device.' :
      'To protect your account, we\'ll send a temporary code to your email.<br>' +
      '<br>Enter it on the next screen to sign in with this device.',
      'Send Code',
      '',
      'Cancel',
      false,
      'sendCode',
      '',
      'close'
    );
  }

//  public async showBlockedDeviceModal() {
//     await this.confirmModalService.show(
//       `This device is blocked to protect your account`,
//       ``,
//       `To use it, please contact Customer Care.`,
//       `Contact Customer Care`,
//       ``,
//       `Cancel`,
//       true,
//       'customerCare',
//       ``,
//       `close`
//     );
// }

public async showOKModal(errorCode: any) {
  const errorMessage = ErrorCodesService.getErrorMessage(errorCode, null);
  const gtmModalTracking = new ModalTrackingGtmDto();
  gtmModalTracking.pageName = GtmExternal.VALUE_SIGNIN;
  gtmModalTracking.event = GtmExternal.EVENT_ERROR;
  let errorSubHeader;
  if (errorCode === MFAErrorcodeEnum.InvalidMFAToken) {
    gtmModalTracking.modal = GtmExternal.VALUE_MODAL_INVALID;
    errorSubHeader = 'To receive a new one, please sign in again.';
  } else if (errorCode === MFAErrorcodeEnum.MFATokenAlreadyVerified || errorCode === MFAErrorcodeEnum.MFATokenExpired) {
    gtmModalTracking.modal = GtmExternal.VALUE_MODAL_CODE_EXPIRED;
    errorSubHeader = 'To receive a new one, please sign in again. ';
  } else if (errorCode === MFAErrorcodeEnum.MFATokenLockedOut) {
    gtmModalTracking.event = GtmExternal.EVENT_DEVICE_LOCKED;
    gtmModalTracking.modal = GtmExternal.VALUE_MODAL_ACC_LOCK;
    errorSubHeader = 'You may sign in again after 30 minutes.';
  } else if (errorCode === MFAErrorcodeEnum.MFADeviceIdIsNotSame) {
    gtmModalTracking.modal = GtmExternal.VALUE_MODAL_WRONG_DEVICE;
  }
 this.gtmService.sendModalEvent(gtmModalTracking);
  // else if (errorCode === 4038) {
  //   errorSubHeader = 'To continue, please sign in on this device and follow the instructions on screen.';
  // }
  await this.confirmModalService.show(
    errorMessage,
    '',
    errorSubHeader,
    'OK, Got It​',
    '',
    '',
    true
  );
}
  public getMFAToken(): MFATokenPayload {
    return this.mfaToken;
  }

  public setMFAToken(token: MFATokenPayload): void {
    this.mfaToken = token;
  }

  public async requestMFACode(isResend?: boolean) {
    if (this.mfaToken) {

      await this.proxy.httpPost(ApiRoutes.MFA_REQUEST_CODE, this.mfaToken).toPromise().then(
        (success) => {
          if (!isResend) {
            this.showModal('app-signin-code');
          }
        },
        async (error) => {
          if (error.error.code === 4034 || error.error.code === 4035 ||
             error.error.code === 4036 || error.error.code === 4037) {
            await this.showOKModal(error.error.code);
            this.modalService.hideModal();
          } else {
            const errorMessage = ErrorCodesService.getErrorMessage(error.error.code, null);
            if (errorMessage) {
              this.alertService.error(errorMessage);
            } else {
              this.alertService.error(error.error.message);
            }
          }
          this.logger.logError(error);
        }
      );
    }
  }

  public confirmMFACode(code: string): void {
    if (this.mfaToken && code) {
      const body = {
        'token': this.mfaToken,
        'code': code
      };

      this.proxy.httpPost(ApiRoutes.MFA_CONFIRM_CODE, body).subscribe(
        (success) => {
          console.log(`Succesfully confirmed code: ${JSON.stringify(success)}`);
        },
        (error) => {
          this.alertService.error(error.error.message);
          this.logger.logError(error);
        }
      );
    }
  }

  setUserData(signInResponse: AuthResponse): void {
    
    signInResponse.user.isEmailVerified = this.getEmailVerificationStatus(signInResponse.user.isEmailVerified);
    this.ngRedux.dispatch({ type: Actions.USERLOGGEDIN, data: signInResponse });
    this.updateBrazeUser(signInResponse.user);
    const guestUser = new Store.CustomerInfo();
    this.ngRedux.dispatch({ type: Actions.GUESTLOGIN, data: guestUser});
  }

  /**
   * After Auth0 OTP verify (JIT migration), replace Thinktecture tokens with Auth0 tokens,
   * fetch profile, update Braze, then run callback (e.g. hide modal).
   */
  setAuth0TokensFromOtpVerify(result: { accessToken?: string; refreshToken?: string; expiresIn?: number; tokenType?: string }, callback?: () => void): void {
    const tokenInfo: Store.TokenInfo = {
      accessToken: result.accessToken ?? null,
      refreshToken: result.refreshToken ?? null,
      expiresIn: result.expiresIn ?? null,
      tokenType: result.tokenType ?? 'Bearer'
    };
    this.ngRedux.dispatch({ type: Actions.USERTOKENREFRESHED, data: tokenInfo });
    this.proxy.httpGet(ApiRoutes.PROFILE).subscribe(
      (success) => {
        const user = success.body as User;
        if (user) {
          user.isEmailVerified = this.getEmailVerificationStatus(user.isEmailVerified);
          this.ngRedux.dispatch({ type: Actions.UPDATEUSERPROFILE, data: user });
          this.updateBrazeUser(user);
        }
        callback?.();
      },
      () => callback?.()
    );
  }

  async handleAppleSignInRedirect(params: Params): Promise<AppleRedirectResult> {
    const errorSomethingWentWrong = 'Something went wrong with your Apple Sign In. Please try again.';
    const errorCancelledAppleSignIn = 'You have cancelled your Apple Sign In.';
    if (params[Constants.APPLE_SIGNIN_ORIGIN] && (params[Constants.APPLE_SIGNIN_ORIGIN]?.toLowerCase() === Constants.SIGNIN || params[Constants.APPLE_SIGNIN_ORIGIN]?.toLowerCase() === Constants.REGISTER)
       && params[Constants.ACCESS_TOKEN]) {
      const localNonce = localStorage.getItem('apple-signin-nonce');
      if (!params[Constants.NONCE]) {
        this.clearGuestOrderContext();
        this.alertService.error(errorSomethingWentWrong);
        this.errorMessage = errorSomethingWentWrong;
        return { status: 'error' };
      } else if (params[Constants.NONCE] && localNonce !== params[Constants.NONCE]) {
        this.clearGuestOrderContext();
        this.alertService.error(errorSomethingWentWrong);
        this.errorMessage = errorSomethingWentWrong;
        return { status: 'error' };
      } else if (params[Constants.NONCE] && localNonce === params[Constants.NONCE]) {
        this.tryRestoreGuestOrderContext();
        const origin = (params[Constants.APPLE_SIGNIN_ORIGIN] as string)?.toLowerCase();
        const appleIdToken = params[Constants.ID_TOKEN] as string | undefined;
        if (origin === Constants.SIGNIN) {
          return this.appleSignInRegister(params[Constants.ACCESS_TOKEN], false, true, appleIdToken);
        }
        return this.appleSignInRegister(params[Constants.ACCESS_TOKEN], true, false, appleIdToken);
      }
    } else if (params[Constants.CANCELLED] === 'true') {
      this.clearGuestOrderContext();
      this.alertService.error(errorCancelledAppleSignIn);
      this.errorMessage = errorSomethingWentWrong;
      return { status: 'error' };
    } else if (params[Constants.SUCCESS] === 'false' && params[Constants.APPLE_SIGNIN_ORIGIN]) {
      this.clearGuestOrderContext();
      this.alertService.error(errorSomethingWentWrong);
      this.errorMessage = errorSomethingWentWrong;
      return { status: 'error' };
    } else {
      this.clearGuestOrderContext();
      this.alertService.error(errorSomethingWentWrong);
      this.errorMessage = errorSomethingWentWrong;
      return { status: 'error' };
    }
  }

  cleanUpAppleSigninQueryParams() {
    localStorage.removeItem('apple-signin-nonce');    
      if(this.router.url.indexOf(Constants.RETURN_URL) !== -1 && decodeURIComponent(this.router.url) !== this.router.url) {
        const urlParams = new URLSearchParams(decodeURIComponent(this.router.url));
        let returnUrl;
        if(urlParams.has(Constants.RETURN_URL)) {
          returnUrl = urlParams.get(Constants.RETURN_URL);
        }
        // special scenario for transfer
        if(urlParams.has(Constants.TRANSFER)) {
          returnUrl+= `&${Constants.TRANSFER}=${(urlParams.get(Constants.TRANSFER).indexOf('true') !== -1).toString()}`
        }       
        this.router.navigateByUrl(returnUrl);        
      }
      else {
        this.router.navigate([], this.getAppleSignInQueryParams());
      }
  }
}
